import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';

import { Text, Button, OTPInput } from '@restworld/ui-ds-base';
import { ModalHeader, ModalSubHeader } from '../common';
import { phoneNumberAtom, useVerifyPhoneNumberForm } from '../../hooks/use-verify-phone-number-form';
import { StyledLink } from '../../../signup/signup-form';
import { Controller } from 'react-hook-form';
import { useIntercom } from 'react-use-intercom';
import { useVerifyOtpForm } from '../../hooks/use-verify-otp-code';
import { useCountDownTimer } from '@restworld/utils-common';

export function useValidateOtpContent({ phoneNumber }: { phoneNumber: string }) {
  const { show: showIntercom } = useIntercom();
  const {
    loading,
    onSubmit,
    handleSubmit,
    control,
    errors,
    serverValidationFailed,
    setServerValidationFailed,
    isSuccess,
  } = useVerifyOtpForm();

  const { loading: requestOTPLoading, requestNewOTPCode } = useVerifyPhoneNumberForm({
    phoneNumber,
  });
  const phoneNumberFormValue = useAtomValue(phoneNumberAtom);
  const { hasExpired, label, resetTimer } = useCountDownTimer({ timeInSeconds: 30 });
  const { t } = useTranslation('user-settings');

  const validateOtpContent = useMemo(
    () => (
      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center justify-start gap-2 mb-4">
          <ModalHeader>{t('validate_otp_content_header')}</ModalHeader>
          <ModalSubHeader>
            {t('validate_otp_content_subheader')} {phoneNumberFormValue}
          </ModalSubHeader>
        </div>
        <form className="w-full">
          <div className="flex flex-col items-center w-full my-10 gap-4">
            <Controller
              name="otpCode"
              control={control}
              rules={{
                required: t('validate_otp_insert_code_prompt'),
                validate: (value) => value?.toString().length === 6,
              }}
              render={({ field }) => (
                <OTPInput
                  onChange={(otp) => {
                    field.onChange(otp);
                    serverValidationFailed && setServerValidationFailed(false);
                    // Verify as soon as the sixth digit lands, without waiting for the CTA.
                    // ui-ds-base OTPInput has no onComplete; drive auto-submit from onChange.
                    // The in-flight guard stops a code edited mid-request from firing twice.
                    if (otp?.toString().length === 6 && !loading) handleSubmit(onSubmit)();
                  }}
                  variant={errors['otpCode'] ? 'error' : 'primary'}
                  value={field?.value?.toString()}
                  isSuccess={isSuccess}
                />
              )}
            />

            {(serverValidationFailed || errors['otpCode']) && (
              <Text align="center" color="error">
                {serverValidationFailed && (
                  <span>
                    {t('validate_otp_content_code_invalid')}{' '}
                    <StyledLink
                      className="text-red-600"
                      onClick={(e) => {
                        e.preventDefault();
                        showIntercom();
                      }}
                    >
                      {t('validate_otp_content_contact_assistance')}
                    </StyledLink>
                  </span>
                )}
                {errors['otpCode'] && !serverValidationFailed && errors['otpCode']?.message}
              </Text>
            )}
          </div>
        </form>
      </div>
    ),
    [
      t,
      phoneNumberFormValue,
      control,
      serverValidationFailed,
      errors,
      isSuccess,
      setServerValidationFailed,
      showIntercom,
      handleSubmit,
      onSubmit,
      loading,
    ]
  );
  const buttonTitle = `${t('validate_otp_content_resend_code_text_1')}  ${
    !hasExpired ? `${t('validate_otp_content_resend_code_text_2')} ${label}` : ''
  }`;
  const validateOtpFooter = useMemo(
    () => (
      <div className="flex flex-col items-center w-full gap-4">
        <Button fullwidth onClick={handleSubmit(onSubmit)} loading={loading}>
          {t('validate_otp_content_complete_verification')}
        </Button>
        <Button
          fullwidth
          disabled={!hasExpired}
          variant="secondary-neutral"
          loading={requestOTPLoading}
          onClick={() => {
            resetTimer();
            requestNewOTPCode();
          }}
        >
          {buttonTitle}
        </Button>
      </div>
    ),
    [handleSubmit, onSubmit, loading, t, hasExpired, requestOTPLoading, buttonTitle, resetTimer, requestNewOTPCode]
  );

  return {
    validateOtpContent,
    validateOtpFooter,
  };
}
